/**
 * LVS 77-3:2016 background-geometry rules.
 *
 * Tables 9.14 and 9.16 use upper-inclusive bands: a diagonal D falls in
 * the first band whose `maxDiagM` is ≥ D (i.e. `diagM <= maxDiagM`).
 *
 * Table 9.15's first band is upper-exclusive (D < 0.90); all subsequent
 * bands are upper-inclusive. The data stores `maxDiagM` for each band;
 * the lookup uses `diagM < band.maxDiagM` for band[0] and
 * `diagM <= band.maxDiagM` for the rest.
 */

/** Section 9.20 — content-to-border min distance as a coefficient of h. */
export const PADDING_COEF = 0.5

/** Section 9.1 tabula — line-spacing coefficient (b = 11/7 × h). */
export const LINE_COEF = 11 / 7

interface Band14 {
    readonly maxDiagM: number
    readonly contrastBorder: number
}

interface Band15 {
    readonly maxDiagM: number
    readonly blackEdge: number
    readonly contrastBorder: number
}

interface Band16 {
    readonly maxDiagM: number
    readonly cornerRadius: number
}

const TABLE_9_14: readonly Band14[] = [
    {maxDiagM: 0.75, contrastBorder: 10},
    {maxDiagM: 1.15, contrastBorder: 15},
    {maxDiagM: 1.55, contrastBorder: 20},
    {maxDiagM: 1.95, contrastBorder: 25},
    {maxDiagM: 2.35, contrastBorder: 30},
    {maxDiagM: 2.75, contrastBorder: 35},
    {maxDiagM: 3.35, contrastBorder: 40},
    {maxDiagM: 4.1, contrastBorder: 50},
    {maxDiagM: 4.9, contrastBorder: 60},
    {maxDiagM: 5.7, contrastBorder: 70},
    {maxDiagM: 6.5, contrastBorder: 80},
    {maxDiagM: 7.3, contrastBorder: 90},
    {maxDiagM: Infinity, contrastBorder: 100}
]

// Band[0] upper bound is exclusive (D < 0.90); all others are inclusive.
const TABLE_9_15: readonly Band15[] = [
    {maxDiagM: 0.9, blackEdge: 10, contrastBorder: 10}, // [0,    0.90)
    {maxDiagM: 1.35, blackEdge: 15, contrastBorder: 10}, // [0.90, 1.35]
    {maxDiagM: 1.8, blackEdge: 20, contrastBorder: 15}, // (1.35, 1.80]
    {maxDiagM: 2.25, blackEdge: 25, contrastBorder: 15}, // (1.80, 2.25]
    {maxDiagM: 3.35, blackEdge: 30, contrastBorder: 20}, // (2.25, 3.35]
    {maxDiagM: 4.0, blackEdge: 45, contrastBorder: 30}, // (3.35, 4.00]
    {maxDiagM: 4.45, blackEdge: 50, contrastBorder: 35}, // (4.00, 4.45]
    {maxDiagM: 4.9, blackEdge: 55, contrastBorder: 35}, // (4.45, 4.90]
    {maxDiagM: 6.25, blackEdge: 60, contrastBorder: 40}, // (4.90, 6.25]
    {maxDiagM: Infinity, blackEdge: 65, contrastBorder: 45} // (6.25, ∞)
]

const TABLE_9_16: readonly Band16[] = [
    {maxDiagM: 1.8, cornerRadius: 45},
    {maxDiagM: 3.35, cornerRadius: 60},
    {maxDiagM: Infinity, cornerRadius: 120}
]

/** Contrast-border width (mm) for a sign whose outer diagonal is `diagM` metres. */
export function table9_14(diagM: number): number {
    for (const band of TABLE_9_14) {
        if (diagM <= band.maxDiagM) return band.contrastBorder
    }
    return TABLE_9_14[TABLE_9_14.length - 1].contrastBorder
}

/** Black-edge + contrast-border widths (mm) for a light-background sign. */
export function table9_15(diagM: number): {blackEdge: number; contrastBorder: number} {
    // Band[0] is upper-exclusive; remaining bands are upper-inclusive.
    if (diagM < TABLE_9_15[0].maxDiagM) {
        return {blackEdge: TABLE_9_15[0].blackEdge, contrastBorder: TABLE_9_15[0].contrastBorder}
    }
    for (let i = 1; i < TABLE_9_15.length; i++) {
        if (diagM <= TABLE_9_15[i].maxDiagM) {
            return {
                blackEdge: TABLE_9_15[i].blackEdge,
                contrastBorder: TABLE_9_15[i].contrastBorder
            }
        }
    }
    const last = TABLE_9_15[TABLE_9_15.length - 1]
    return {blackEdge: last.blackEdge, contrastBorder: last.contrastBorder}
}

/** Corner radius (mm). */
export function table9_16(diagM: number): number {
    for (const band of TABLE_9_16) {
        if (diagM <= band.maxDiagM) return band.cornerRadius
    }
    return TABLE_9_16[TABLE_9_16.length - 1].cornerRadius
}
